import {
  ArrowBackRounded,
  EditOutlined,
  KeyboardArrowRight,
  ReportProblemRounded,
  SearchRounded,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
  TextField as MuiTextField,
  InputAdornment,
  Tab,
  Tabs,
} from "@mui/material";
import { DataGrid, GridRenderCellParams } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import FilterIcon from "../../assets/filter_icon.svg";
import PDFIcon from "../../assets/pdf_icon.svg";
import XLSIcon from "../../assets/xls_icon.svg";
import Loading from "../../components/loading/loading";
import MainLayout from "../../layouts/main/main_layout";
import { downloadNeeds, getNeeds } from "../../services/needs/needs_service";
import NeedFilters from "./components/needs_filter/needs_filter";
import { downloadFromBlob } from "../../utils/utils";
import { black, greyEnabled, orange, purple, purpleLight } from "../../theme/colors";
import useUser from "../../providers/user/user_provider";
import AccessDenied from "../../components/access_denied/access_denied";
import { RECORDS_EXCEEDS_ALERT_MSG } from "../../constants";
import { useFeedback } from "../../providers/feedback/feeedback";
import { getStateMasterData } from "../../services/master/master_service";
import styled from "@emotion/styled";
import NeedsBucketing from "./incident_grouping";
import { useNeedFiltersStore } from "../../store/use-needs-filter-store";
import { NeedsColumns } from "../../constants/needs-columns";
import { useActiveNeedFilterCount } from "../../store/use-needs-filter-store";
import { ToolbarButton } from "../../components/toolbar_button/toolbar_button";
import OrgSelector from "../../components/org_selector/org_selector";
import { useSelectedOrgStore } from "../../store/use-selected-org-store";
import SortIcon from "../../assets/sort.svg";
import SortNeedsPopup, {
  DEFAULT_NEEDS_SORT_OPTION,
  NeedsSortOption,
  needsSortOptionLabel,
  needsSortOptionToParams,
} from "./components/sort_needs_popup/sort_needs_popup";
import { BORDER_COLOR } from "../users/components/table_toolkit/table_toolkit";

// Shared with the rest of the app's bordered-table design.
const DATA_GRID_BORDER_COLOR = BORDER_COLOR;

const HEADER_ICON_BADGE = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 44,
  height: 44,
  borderRadius: 2,
  bgcolor: purpleLight,
  flexShrink: 0,
} as const;

const TextField = styled(MuiTextField)(() => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    "& fieldset": { borderColor: "#ABA0AB" },
    "&:hover fieldset": { borderColor: purple },
    "&.Mui-focused fieldset": { borderColor: purple, borderWidth: 2 },
  },
}));

export default function Needs() {
  const { userPermissions, hasPermission } = useUser();
  const [searchParams, setUrlParam] = useSearchParams();
  // const [paginationModel, setPaginationModel] = useState({
  //   pageSize: 10,
  //   page: searchParams.has("page")
  //     ? parseInt(searchParams?.get("page")!)
  //     : 1,
  // });

  const { paginationModel, setPaginationModel } = useNeedFiltersStore();
  const {
    districtId,
    stateId,
    areasId,
    concerningToId,
    status,
    cityId,
    subcategoryId,
    brandId,
    startDate,
    endDate,
    searchValue,
    setSearchValue

  } = useNeedFiltersStore()
  const activeFilterCount = useActiveNeedFilterCount()
  const { selectedOrgId } = useSelectedOrgStore();
  const [filterDialogOpen, setFilterDialogOpen] = useState<boolean>(false);
  // const [concerningToId, setConcerningToId] = useState<number | null>(null);
  // const [subcategoryId, setSubcategoryId] = useState<number | null>(null);
  // const [areasId, setAreasId] = useState<number | null>(null);
  // const [brandId, setBrandId] = useState<number | null>(null);
  // const [status, setStatus] = useState<number | null>(null);
  // const [cityId, setCityId] = useState<number | null>(null);
  // const [startDate, setStartDate] = useState<string | null>(null);
  // const [endDate, setEndDate] = useState<string | null>(null);
  const [sortOption, setSortOption] = useState<NeedsSortOption>(DEFAULT_NEEDS_SORT_OPTION);
  const [sortDialogOpen, setSortDialogOpen] = useState(false);
  // const [stateId, setStateId] = useState<number | null>(null);
  // const [districtId, setDistrictId] = useState<number | null>(null);
  // const [searchValue, setSearchValue] = useState("");
  const { showSnackbar } = useFeedback();
  const [currentTab, setCurrentTab] = React.useState(searchParams?.has("currentTab") ?
    parseInt(searchParams?.get('currentTab')!) : 0);
  const [selectedLabel, setSelectedLabel] = useState('')

  useEffect(() => {
    setUrlParam({
      page: (paginationModel.page).toString(),
      currentTab: currentTab!.toString(), ...searchParams,
      labelling: 'labelList'
    });
  }, [paginationModel, searchParams, currentTab, setUrlParam]);

  useEffect(() => {
    setPaginationModel({ ...paginationModel, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId, sortOption]);

  // Only needed to populate the filter dialog's State dropdown — fetching it
  // eagerly on every Needs page load was an unnecessary call for anyone who
  // never opens the filter dialog.
  //
  // Query key must NOT be the plain "masterDataList" used elsewhere (Users,
  // Rewards, Schemes enquiries, Client Mapping) for getMasterData() - that
  // fetches a completely different shape (a {gender, maritalStatus, ...}
  // object, not this array). Sharing the key meant navigating from one of
  // those pages into Needs could render the other query's stale cached
  // object here, crashing `states.map` in NeedFiltersModel. Matches the
  // "masterDataList-incident-labelling" key already used for this exact
  // same call in incident_grouping_needs_list.tsx.
  const { data: masterDataList, isLoading: statesLoading } = useQuery(["needs-state-master-list"], async () => {
    let response = await getStateMasterData();
    return response?.data?.data?.list;
  }, { enabled: filterDialogOpen });

  const handleChange = (event: React.SyntheticEvent, currentTabVal: number) => {
    setCurrentTab(currentTabVal);
  };

  const navigate = useNavigate();

  const handleBackClick = () => {
    const currPage = Number(searchParams.get("page")) || 1;

    if (currPage > 1) {
      const newPage = currPage - 1;
      setPaginationModel({ ...paginationModel, page: newPage });
      setUrlParam(prev => {
        const params = new URLSearchParams(prev);
        params.set("page", (newPage + 1).toString());
        return params;
      }, { replace: true });

    }
  };

  const handlePageChange = (pagination: any) => {
    setPaginationModel({ ...pagination, page: pagination.page + 1 })
  }
  // All filter/sort/pagination dependencies live in the query key (matching
  // the pattern used in users.tsx) so react-query refetches automatically
  // whenever any of them change or the page is stale, instead of relying on
  // a manually-toggled "applyFilter" flag + mount effect — that indirection
  // was fragile (e.g. under React 18 StrictMode's dev-mode double-effect
  // invocation, the trigger could desync from the actual fetch), and result
  // data is read directly from the query (not copied into local state) so
  // cached data survives remounting this page (e.g. navigating to need
  // details and back) instead of momentarily/permanently showing an empty
  // list.
  const { data, isFetching, isError } = useQuery(
    [
      "needs",
      paginationModel,
      concerningToId,
      subcategoryId,
      areasId,
      status,
      cityId,
      startDate,
      endDate,
      sortOption,
      stateId,
      searchValue,
      districtId,
      selectedLabel,
      selectedOrgId,
    ],
    async () => {
      const { sortBy, sortOrder } = needsSortOptionToParams(sortOption);
      const response = await getNeeds({
        paginationModel,
        concerningToId,
        subcategoryId,
        areasId,
        status,
        cityId,
        startDate,
        endDate,
        sortBy,
        sortOrder,
        state: stateId,
        keyword: searchValue,
        district: districtId,
        label: selectedLabel,
        orgId: selectedOrgId,
      });

      return response?.data;
    },
    {
      retry: false,

      onError: (error: any) => {
        showSnackbar(error?.response?.data?.error?.message || "Something went wrong while loading needs.");
      },
    }
  );

  const needsList = data?.data?.data?.list || [];
  const needsTotal = data?.data?.data?.total || 0;

  function loading() {
    return (
      <Box
        height={300}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Loading />
      </Box>
    );
  }

  function searchComponent() {
    return (
      <TextField
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
        }}
        size="small"
        fullWidth
        sx={{
          maxWidth: 420,
          backgroundColor: purpleLight,
          borderRadius: 3,
          borderColor: "transparent",
        }}
        placeholder="Search"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded sx={{ color: greyEnabled }} />
            </InputAdornment>
          ),
        }}
      />
    );
  }
  function downloadPdf() {
    const { sortBy, sortOrder } = needsSortOptionToParams(sortOption);
    if (needsTotal <= 1000) {
      downloadNeeds({
        page: 1,
        size: 1000,
        concerningToId,
        subcategoryId,
        areasId,
        status,
        cityId,
        startDate,
        endDate,
        format: "pdf",
        sortBy,
        sortOrder,
        state: stateId,
        label: '',
        orgId: selectedOrgId
        // keyword: searchValue
      }).then((response) => {
        downloadFromBlob(response, new Date().valueOf() + ".pdf");
      });
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
  }

  function downloadExcel() {
    const { sortBy, sortOrder } = needsSortOptionToParams(sortOption);
    if (needsTotal <= 1000) {
      downloadNeeds({
        page: 1,
        size: 1000,
        concerningToId,
        subcategoryId,
        areasId,
        status,
        cityId,
        startDate,
        endDate,
        format: "excel",
        sortBy,
        sortOrder,
        state: stateId,
        label: '',
        orgId: selectedOrgId
      }).then((response) => {
        downloadFromBlob(response, new Date().valueOf() + ".xlsx");
      });
    } else {
      showSnackbar(RECORDS_EXCEEDS_ALERT_MSG);
    }
  }

  return (
    <MainLayout>
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        {userPermissions?.superAdmin && paginationModel.page > 1 && (
          <IconButton onClick={handleBackClick}>
            <ArrowBackRounded />
          </IconButton>
        )}
        <Box sx={HEADER_ICON_BADGE}>
          <ReportProblemRounded sx={{ color: purple, fontSize: 24 }} />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} color={black}>
            Needs
          </Typography>
          <Typography variant="body2" color={greyEnabled}>
            Track and triage reported needs and incidents.
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={3}>
        <Grid item md={12} sm={12} xs={12} lg={12} xl={12}>
          {hasPermission("needs", "read") ? (
            <Card
              elevation={0}
              sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC" }}
            >
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                {/* Incident Labeling tab hidden — not required for now. Keeping the
                markup (and the NeedsBucketing render below) commented instead of
                removed in case it's needed again.
                <Tabs
                  value={currentTab}
                  aria-label="needs tabs"
                  onChange={handleChange}
                  TabIndicatorProps={{
                    style: { backgroundColor: purple, height: 3, borderRadius: 3 },
                  }}
                  sx={{ borderBottom: "1px solid #ECECEC", mb: currentTab === 0 ? 2 : 0 }}
                >
                  <Tab
                    label="All"
                    sx={{
                      textTransform: "capitalize",
                      fontWeight: 600,
                      color: black,
                    }}
                  />
                  <Tab
                    label="Incident Labeling"
                    sx={{ textTransform: "capitalize", fontWeight: 600, color: black }}
                  />
                </Tabs>
                */}
                {currentTab === 0 && <>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={2}
                    mb={2}
                  >
                    {searchComponent()}
                    <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
                      <OrgSelector lockToOwnOrg />
                      <ToolbarButton icon={XLSIcon} label="Export Excel" onClick={downloadExcel} />
                      <ToolbarButton icon={PDFIcon} label="Export PDF" onClick={downloadPdf} />
                      <ToolbarButton
                        icon={SortIcon}
                        label={needsSortOptionLabel(sortOption)}
                        onClick={() => setSortDialogOpen(true)}
                      />
                      <ToolbarButton
                        icon={FilterIcon}
                        label="Filters"
                        badgeCount={activeFilterCount}
                        onClick={() => setFilterDialogOpen(true)}
                      />
                    </Box>
                  </Box>
                  <Box overflow="auto" flex={1}>
                    {isFetching || data === undefined ? (
                      loading()
                    ) : isError ? (
                      <Box
                        height={300}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                      >
                        <ReportProblemRounded sx={{ color: greyEnabled, fontSize: 32 }} />
                        <Typography color={greyEnabled}>
                          Something went wrong while loading needs.
                        </Typography>
                      </Box>
                    ) : !needsList.length ? (
                      <Box
                        height={300}
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                      >
                        <SearchRounded sx={{ color: greyEnabled, fontSize: 32 }} />
                        <Typography color={greyEnabled}>No needs found.</Typography>
                      </Box>
                    ) : (
                      <Box display="flex" width="100%" alignItems="stretch">
                        <DataGrid
                          paginationModel={{ ...paginationModel, page: paginationModel.page - 1 }}
                          paginationMode="server"
                          rowCount={needsTotal}
                          onPaginationModelChange={handlePageChange}
                          pageSizeOptions={[10, 25, 50, 100]}
                          filterMode="client"
                          sx={{
                            border: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                            borderTopLeftRadius: 8,
                            borderBottomLeftRadius: 8,
                            borderTopRightRadius: 0,
                            borderBottomRightRadius: 0,
                            borderRight: "none",
                            flex: 1,
                            "& .MuiDataGrid-columnHeaders": {
                              bgcolor: purpleLight,
                              color: "#91278F",
                              borderBottom: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                            },
                            "& .MuiDataGrid-columnHeaderTitle": {
                              fontWeight: 700,
                            },
                            "& .MuiDataGrid-columnHeader": {
                              borderRight: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                            },
                            "& .MuiDataGrid-columnHeader:last-of-type": {
                              borderRight: "none",
                            },
                            "& .MuiDataGrid-cell": {
                              borderRight: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                              borderBottom: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                            },
                            "& .MuiDataGrid-cell:last-of-type": {
                              borderRight: "none",
                            },
                            "& .MuiDataGrid-row": {
                              borderLeft: "none",
                            },
                          }}
                          rows={needsList}
                          autoHeight
                          getRowId={(row) => row?.needId || 0}
                          onRowClick={(row) => {
                            navigate(`/needs/${row.id}`);
                          }}
                          columns={NeedsColumns.map((column) =>
                            column.field === "systemSubCompany" ||
                              column.field === "systemSubCategory"
                              ? {
                                ...column,
                                renderCell: (params: GridRenderCellParams) => {
                                  if (
                                    params.row.systemCategory === "Corporate" &&
                                    column.field === "systemSubCompany"
                                  ) {
                                    return (
                                      <Box>
                                        <Typography variant="subtitle2">
                                          {params.row.systemSubCategory}
                                        </Typography>
                                      </Box>
                                    );
                                  } else if (
                                    params.row.systemCategory !== "Corporate" &&
                                    column.field === "systemSubCategory"
                                  ) {
                                    return (
                                      <Box>
                                        <Typography variant="subtitle2">
                                          {params.row.systemSubCategory}
                                        </Typography>
                                      </Box>
                                    );
                                  } else {
                                    return (
                                      <Box>
                                        <Typography variant="subtitle2">
                                          NA
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                },
                              } // Apply the custom component for the 'name' column
                              : column
                          )}
                        />
                        <Box
                          width={250}
                          position="relative"
                          overflow="hidden"
                        >
                          <DataGrid
                            hideFooter={true}
                            filterMode="client"
                            sx={{
                              zIndex: 9999,
                              border: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                              borderTopRightRadius: 8,
                              borderBottomRightRadius: 8,
                              borderTopLeftRadius: 0,
                              borderBottomLeftRadius: 0,
                              flex: 1,
                              overflow: "hidden",
                              "& .MuiDataGrid-columnHeaders": {
                                bgcolor: purpleLight,
                                color: "#91278F",
                                borderBottom: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                              },
                              "& .MuiDataGrid-columnHeaderTitle": {
                                fontWeight: 700,
                              },
                              "& .MuiDataGrid-cell": {
                                borderBottom: `1px solid ${DATA_GRID_BORDER_COLOR}`,
                              },
                              "& .MuiDataGrid-row": {
                                borderLeft: "none",
                              },
                            }}
                            rows={needsList}
                            getRowId={(row: any) => row?.needId || 0}
                            components={{
                              NoResultsOverlay: (props) => {
                                return <Box></Box>;
                              },
                            }}
                            columns={[
                              {
                                field: "status",
                                headerName: "Status",
                                width: 200,
                                sortable: false,
                                filterable: false,
                                renderCell: (row) => {
                                  return (
                                    <Box
                                      display="flex"
                                      width="100%"
                                      alignItems="center"
                                    >
                                      <Typography
                                        textAlign={"center"}
                                        flex={1}
                                        color={
                                          row.value === "Closed"
                                            ? "success.main"
                                            : row.value === "In Progress"
                                              ? orange
                                              : "text.primary"
                                        }
                                        variant="body2"
                                        fontWeight={600}
                                      >
                                        {row.value}
                                        <EditOutlined
                                          sx={{ width: 15, height: 15, ml: 1 }}
                                        />
                                      </Typography>
                                      <IconButton>
                                        <KeyboardArrowRight
                                          sx={{ color: "#D14C2E" }}
                                        />
                                      </IconButton>
                                    </Box>
                                  );
                                },
                              },
                            ]}
                            autoHeight
                            onRowClick={(row) => {
                              navigate(`/needs/${row.id}`);
                            }}
                          />
                          <Box
                            position="absolute"
                            bottom={0}
                            left={0}
                            width="100%"
                            height={40}
                            bgcolor="white"
                          ></Box>
                        </Box>
                      </Box>
                    )}
                  </Box>

                </>}
                {/* currentTab === 1 && <NeedsBucketing /> */}
              </CardContent>
            </Card>
          ) : (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              height={600}
            >
              <AccessDenied />
            </Box>
          )}
        </Grid>
      </Grid>
      <SortNeedsPopup
        open={sortDialogOpen}
        onClose={() => setSortDialogOpen(false)}
        sortOption={sortOption}
        setSortOption={setSortOption}
      />
      {filterDialogOpen ? (
        <NeedFilters
          states={masterDataList || []}
          statesLoading={statesLoading}
          open={filterDialogOpen}
          onDialogClose={() => {
            setFilterDialogOpen(false);
            // setPaginationModel({ ...paginationModel, page: 1 });
          }}
        />
      ) : null}
    </MainLayout>
  );
}
